import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type Auth,
  type User,
} from "firebase/auth";
import { ENV } from "@/util/env";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

function init(): Auth {
  if (auth) return auth;
  if (!ENV.FIREBASE_API_KEY || !ENV.FIREBASE_PROJECT_ID) {
    throw new Error(
      "Firebase is not configured — set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID (and VITE_FIREBASE_APP_ID) in apps/web/.env, then rebuild with `make build-web`.",
    );
  }
  app = initializeApp({
    apiKey: ENV.FIREBASE_API_KEY,
    authDomain: ENV.FIREBASE_AUTH_DOMAIN,
    projectId: ENV.FIREBASE_PROJECT_ID,
    appId: ENV.FIREBASE_APP_ID || undefined,
  });
  auth = getAuth(app);
  return auth;
}

export const firebaseAuth = {
  async signInEmail(email: string, password: string): Promise<User> {
    const a = init();
    const cred = await signInWithEmailAndPassword(a, email, password);
    return cred.user;
  },
  async signUpEmail(email: string, password: string): Promise<User> {
    const a = init();
    const cred = await createUserWithEmailAndPassword(a, email, password);
    return cred.user;
  },
  async signInGoogle(): Promise<User> {
    const a = init();
    const cred = await signInWithPopup(a, new GoogleAuthProvider());
    return cred.user;
  },
  async sendPasswordReset(email: string): Promise<void> {
    const a = init();
    await sendPasswordResetEmail(a, email);
  },
  async signOut(): Promise<void> {
    const a = init();
    await fbSignOut(a);
  },
  async getIdToken(): Promise<string | null> {
    const a = init();
    const u = a.currentUser;
    if (!u) return null;
    return await u.getIdToken();
  },
  subscribe(cb: (user: User | null) => void): () => void {
    const a = init();
    return onAuthStateChanged(a, cb);
  },
  current(): User | null {
    return auth?.currentUser ?? null;
  },
};

interface FirebaseLikeError {
  code?: string;
  message?: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-email": "That email address isn't valid.",
  "auth/user-not-found": "No account exists for that email.",
  "auth/wrong-password": "Wrong password.",
  "auth/invalid-credential": "Email or password is wrong.",
  "auth/email-already-in-use": "An account already exists for that email — log in instead.",
  "auth/weak-password": "Password must be at least 6 characters.",
  "auth/popup-closed-by-user": "Google sign-in was cancelled.",
  "auth/popup-blocked": "Your browser blocked the Google popup — allow popups for this site.",
  "auth/network-request-failed": "Network problem — check your connection and try again.",
  "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
};

export function mapFirebaseError(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as FirebaseLikeError;
    if (e.code && ERROR_MESSAGES[e.code]) return ERROR_MESSAGES[e.code];
    if (e.message) return e.message;
  }
  return "Something went wrong. Try again.";
}
